// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/UpdateChangeRequestOrder";
import DatePicker, { DatePickerPresentationProps } from "@store/MobiusOverrides/DatePicker/DatePicker";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import React, { FC, useContext, useMemo } from "react";
import InlineAddress, { InlineAddressPresentationProps } from "@store/Library/Components/InlineAddress";
import LocalizedDateTime from "@store/Overrides/Components/LocalizedDateTime";
import SearchShipToField, { SearchShipToFieldPresentationProps } from "@store/Library/Components/SearchShipToField";

export interface OrderChangeRequestInformationProps extends WidgetProps {}

export interface OrderChangeRequestInformationStyles {
    boxBottomWrapper?: InjectableCss;
    boxesWrapper?: InjectableCss;
    boxTopWrapper?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    fullGridItem?: GridItemProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    infoWrapper?: InjectableCss;
    poNumberTextField?: TextFieldPresentationProps;
    shipToInlineAddress?: InlineAddressPresentationProps;
    searchShipToField?: SearchShipToFieldPresentationProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const orderChangeRequestInformationStyles: OrderChangeRequestInformationStyles = {
    wrapper: orderV2Props.sectionWrapper,
    title: orderV2Props.sectionTitle,
    gridContainer: orderV2Props.boxesGridContainer,
    gridItem: { width: [12, 6, 6, 6, 6] },
    fullGridItem: { width: 12 },
    boxesWrapper: orderV2Props.boxesWrapper,
    boxTopWrapper: orderV2Props.boxTopWrapper,
    boxBottomWrapper: orderV2Props.boxBottomWrapper,
    infoWrapper: orderV2Props.infoWrapper,
    infoLabel: orderV2Props.infoLabel,
    infoValue: orderV2Props.infoValue,
};

const styles = orderChangeRequestInformationStyles;

const OrderChangeRequestInformation: FC<OrderChangeRequestInformationProps> = () => {
    const dispatch = useAppDispatch();
    const { value: order } = useContext(OrderStateContext);
    const { cartSettings } = useAppSelector(getSettingsCollection);
    const { changeRequestOrder } = useAppSelector(state => state.greif.pages.orderChange);
    const { newRequestedDate, newPoNumber } = changeRequestOrder;
    const excludedCustomerSequences = useMemo(() => (order ? [order.customerSequence] : []), [order?.customerSequence]);

    const selectedRequestedDate = useMemo(
        () => (newRequestedDate ? new Date(newRequestedDate) : undefined),
        [newRequestedDate],
    );
    const minDate = useMemo(() => new Date(), []);
    const maxDate = useMemo(
        () => addDays(minDate, cartSettings.maximumDeliveryPeriod),
        [minDate, cartSettings.maximumDeliveryPeriod],
    );

    if (!order) {
        return null;
    }

    const updateNewShipTo = (newShipTo?: ShipToModel) => {
        dispatch(
            updateChangeRequestOrder({
                newShipToAddress1: newShipTo?.address1,
                newShipToCity: newShipTo?.city,
                newShipToCode: newShipTo?.customerSequence,
                newShipToCompanyName: newShipTo?.companyName,
                newShipToCountry: newShipTo?.country?.name,
                newShipToPostalCode: newShipTo?.postalCode,
                newShipToState: newShipTo?.state?.name,
            }),
        );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Order Information")}</Typography>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("Requested Date")}</Typography>
                                <Typography {...styles.infoValue}>
                                    {order.requestedDeliveryDateDisplay ? (
                                        <LocalizedDateTime dateTime={order.requestedDeliveryDateDisplay} />
                                    ) : (
                                        "—"
                                    )}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.boxBottomWrapper}
                            data-test-selector="orderChangeRequestNewRequestedDate"
                        >
                            <DatePicker
                                {...styles.datePicker}
                                label={translate("New Requested Date")}
                                onDayChange={({ selectedDay }) => {
                                    if (!selectedDay) {
                                        dispatch(updateChangeRequestOrder({ newRequestedDate: undefined }));
                                        return;
                                    }

                                    const localDate = new Date(
                                        selectedDay.getFullYear(),
                                        selectedDay.getMonth(),
                                        selectedDay.getDate(),
                                        12,
                                        0,
                                        0,
                                    );

                                    const utcDate = new Date(
                                        localDate.getTime() - localDate.getTimezoneOffset() * 60000,
                                    );

                                    dispatch(updateChangeRequestOrder({ newRequestedDate: utcDate }));
                                }}
                                selectedDay={selectedRequestedDate}
                                dateTimePickerProps={{
                                    minDate,
                                    maxDate,
                                }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("PO Number")}</Typography>
                                <Typography {...styles.infoValue}>{order.customerPO || "-"}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.boxBottomWrapper}>
                            <TextField
                                {...styles.poNumberTextField}
                                type="text"
                                onChange={event => {
                                    dispatch(updateChangeRequestOrder({ newPoNumber: event.target.value.trim() }));
                                }}
                                label={translate("New PO Number")}
                                value={newPoNumber ?? ""}
                                data-test-selector="orderChangeRequestNewPoNumber"
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.fullGridItem}>
                    <StyledWrapper {...styles.boxesWrapper}>
                        <StyledWrapper {...styles.boxTopWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.infoLabel}>{translate("Shipping Information")}</Typography>
                                <Typography {...styles.infoValue}>
                                    <InlineAddress {...styles.shipToInlineAddress} shipTo={order} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.boxBottomWrapper}>
                            <SearchShipToField
                                {...styles.searchShipToField}
                                data-test-selector="orderChangeRequestShipToSelector"
                                additionalParameters={{
                                    exclude: ["createNew", "showAll", "oneTime"],
                                    additionalExpands: [],
                                }}
                                clearOnClick={() => updateNewShipTo()}
                                excludedCustomerSequences={excludedCustomerSequences}
                                label={translate("Select New Ship To")}
                                onSelectionChangeShipTo={updateNewShipTo}
                                placeholder={translate("Search or Select Ship To")}
                                selected={changeRequestOrder.newShipToCode || ""}
                                showClear={!!changeRequestOrder.newShipToCode}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestInformation,
    definition: {
        group: constants.widgetGroup.orderChangeRequest,
        displayName: "Order Change Request Information",
        allowedContexts: [constants.pageType.orderChangeRequest],
    },
};

export default widgetModule;
